// ExclusiveHubOffer.jsx - Direct hub purchase (no auction for early game)
// First 2-3 hubs are exclusive offers - no AI competition
// Triggered by: hub capacity (30 aircraft) OR cash >= $50M

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { soundManager, SOUNDS } from "../../systems/SoundManager";
import { GLOBAL_AIRPORTS } from "../../data/GlobalAirports.js";
import { debug } from '../../lib/debug.js';

// Hub pricing for exclusive offers (slightly lower than auction base values)
const EXCLUSIVE_HUB_PRICES = {
  // Tier 1: Nearby affordable hubs (first expansion targets)
  KSEA: 35000000,  // Seattle
  KPDX: 30000000,  // Portland (if somehow not starting hub)
  KSLC: 32000000,  // Salt Lake City
  KLAS: 35000000,  // Las Vegas
  KPHX: 40000000,  // Phoenix
  KSAN: 38000000,  // San Diego

  // Tier 2: Major West Coast
  KSFO: 50000000,  // San Francisco
  KLAX: 55000000,  // Los Angeles
  KDEN: 45000000,  // Denver

  // Tier 3: Major hubs (later game)
  KORD: 58000000,  // Chicago O'Hare
  KDFW: 55000000,  // Dallas
  KATL: 60000000,  // Atlanta
  KJFK: 65000000,  // New York JFK

  // Regional hubs
  KBNA: 28000000,  // Nashville
  KAUS: 30000000,  // Austin
  KMSP: 38000000,  // Minneapolis
  KBOS: 48000000,  // Boston
  KMIA: 50000000,  // Miami
};

const DEFAULT_PRICE = 40000000; // $40M default

// Format currency
const formatMoney = (amount) => {
  if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(2)}B`;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(0)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
};

// Get tier color based on price
const getTierInfo = (price) => {
  if (price <= 35000000) return { tier: "Regional Hub", color: "#22c55e", bgColor: "#052e16" };
  if (price <= 45000000) return { tier: "Growing Hub", color: "#3b82f6", bgColor: "#0c1929" };
  if (price <= 55000000) return { tier: "Major Hub", color: "#a78bfa", bgColor: "#1a0f2e" };
  return { tier: "Mega Hub", color: "#fbbf24", bgColor: "#1c1302" };
};

// Exclusive hub pricing by order SHOWN (not accepted) - makes early expansion feel good
const EXCLUSIVE_ORDER_PRICES = {
  0: 20000000,  // 1st exclusive offer shown: $20M (affordable first expansion)
  1: 45000000,  // 2nd exclusive offer shown: $45M
  2: 50000000,  // 3rd exclusive offer shown: $50M
};

export default function ExclusiveHubOffer({ hub, trigger, onClose, onComplete }) {
  const { worldState, updateWorldState } = useWorldState();
  const playerCash = worldState?.airline?.cash || 0;
  const playerAirlineName = worldState?.airline?.name || "Your Airline";

  const [phase, setPhase] = useState("offer"); // offer -> result
  const [accepted, setAccepted] = useState(false);
  const [offerPrice, setOfferPrice] = useState(null); // Lock in price when offer is shown

  // Get hub info
  const airport = GLOBAL_AIRPORTS[hub] || { name: hub, city: "Unknown" };

  // Price based on how many exclusive offers have been SHOWN (not accepted)
  // This is set once when offer first displays
  useEffect(() => {
    if (offerPrice === null) {
      const offersReceived = worldState?.exclusiveOffersReceived || 0;
      const calculatedPrice = EXCLUSIVE_ORDER_PRICES[offersReceived] ?? (EXCLUSIVE_HUB_PRICES[hub] || DEFAULT_PRICE);
      setOfferPrice(calculatedPrice);

      // Increment counter when offer is SHOWN
      updateWorldState(prev => ({
        ...prev,
        exclusiveOffersReceived: (prev.exclusiveOffersReceived || 0) + 1,
      }));

      debug(`[ExclusiveHubOffer] Offer #${offersReceived + 1} shown for ${hub} at $${(calculatedPrice/1000000).toFixed(0)}M`);
    }
  }, [hub, offerPrice, worldState?.exclusiveOffersReceived, updateWorldState]);

  const price = offerPrice ?? DEFAULT_PRICE;

  const tierInfo = getTierInfo(price);
  const canAfford = playerCash >= price;

  // Determine trigger message
  const triggerMessage = useMemo(() => {
    if (trigger === 'capacity') {
      return "Your hub has reached maximum capacity (30 aircraft). Time to expand!";
    } else if (trigger === 'cash') {
      return "Your airline's financial success has attracted attention from airports seeking new partners.";
    }
    return "An exclusive opportunity has presented itself.";
  }, [trigger]);

  // Handle accept
  const handleAccept = useCallback(() => {
    if (!canAfford) return;

    soundManager.play(SOUNDS.HUB_WON);
    setAccepted(true);
    setPhase("result");

    // Update world state
    const currentHubs = worldState?.airline?.hubs || ["KPDX"];
    const exclusiveHubsAwarded = (worldState?.exclusiveHubsAwarded || 0) + 1;

    updateWorldState({
      airline: {
        ...worldState?.airline,
        cash: playerCash - price,
        hubs: [...currentHubs, hub],
        completedHubAuctions: [...(worldState?.airline?.completedHubAuctions || []), hub],
      },
      // Also update ownedHubs (used by WingsSystem, NewRouteModal, etc.)
      ownedHubs: [...new Set([...(worldState?.ownedHubs || ['KPDX']), hub])],
      exclusiveHubsAwarded,
      // Remove from pending offers
      pendingExclusiveOffers: (worldState?.pendingExclusiveOffers || []).filter(o => o.hub !== hub),
      pendingHubInvites: (worldState?.pendingHubInvites || []).filter(h => h !== hub),
    });

  }, [canAfford, hub, playerCash, price, worldState, updateWorldState]);

  // Handle decline - ALWAYS save to pending offers so player can accept later
  const handleDecline = useCallback(() => {
    const currentPending = worldState?.pendingExclusiveOffers || [];
    const alreadyPending = currentPending.some(o => o.hub === hub);

    // Always save declined offers - player can accept later from Hub Store or pending offers
    if (!alreadyPending) {
      updateWorldState({
        pendingExclusiveOffers: [...currentPending, {
          hub,
          price,
          trigger,
          createdAt: worldState?.gameTime || new Date().toISOString(),
        }],
        pendingHubInvites: (worldState?.pendingHubInvites || []).filter(h => h !== hub),
      });
    } else {
      // Already pending, just remove from active invites
      updateWorldState({
        pendingHubInvites: (worldState?.pendingHubInvites || []).filter(h => h !== hub),
      });
    }

    setPhase("result");
    setAccepted(false);
  }, [hub, price, trigger, worldState, updateWorldState]);

  // Handle close
  const handleClose = useCallback(() => {
    onComplete?.(accepted ? hub : null);
    onClose?.();
  }, [accepted, hub, onComplete, onClose]);

  // Render offer
  const renderOffer = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
          style={{ backgroundColor: "#14532d", border: "1px solid #22c55e" }}
        >
          <span style={{ color: "#86efac" }}>Exclusive Opportunity!</span>
        </div>
        <h2 className="text-3xl font-bold mb-2" style={{ color: tierInfo.color }}>
          {airport.name || hub}
        </h2>
        <p className="text-lg" style={{ color: "#9ca3af" }}>{airport.city}</p>
        <Badge className="mt-2" style={{ backgroundColor: tierInfo.bgColor, color: tierInfo.color, border: `1px solid ${tierInfo.color}40` }}>
          {tierInfo.tier}
        </Badge>
      </div>

      {/* Message */}
      <div className="p-6 rounded-xl text-center" style={{ backgroundColor: "#0c1929", border: "1px solid #3b82f640" }}>
        <p className="text-lg mb-4" style={{ color: "#e5e5e5" }}>
          <span className="font-bold" style={{ color: "#60a5fa" }}>{airport.name}</span> has noticed your airline's success and is reaching out to you <span style={{ color: "#22c55e" }}>exclusively</span> for hub access.
        </p>
        <p className="text-sm" style={{ color: "#9ca3af" }}>
          {triggerMessage}
        </p>
      </div>

      {/* Price box */}
      <div
        className="p-6 rounded-xl text-center"
        style={{ backgroundColor: tierInfo.bgColor, border: `2px solid ${tierInfo.color}60` }}
      >
        <p className="text-sm mb-2" style={{ color: "#9ca3af" }}>Hub Access Price</p>
        <p className="text-4xl font-bold mb-2" style={{ color: tierInfo.color }}>
          {formatMoney(price)}
        </p>
        <p className="text-sm" style={{ color: canAfford ? "#22c55e" : "#ef4444" }}>
          Your Cash: {formatMoney(playerCash)}
          {!canAfford && (
            <span className="block mt-1">
              (Need {formatMoney(price - playerCash)} more)
            </span>
          )}
        </p>
      </div>

      {/* No auction notice */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: "#052e1640", border: "1px solid #22c55e40" }}>
        <p className="text-sm text-center" style={{ color: "#86efac" }}>
          <span style={{ fontWeight: "bold" }}>No Auction Required</span> - This is a direct offer exclusively for you.
          {!canAfford && " The offer will remain available until you can afford it."}
        </p>
      </div>

      {/* Buttons */}
      <div className="flex justify-center gap-4">
        <Button
          onClick={handleDecline}
          className="px-6 py-3"
          style={{ backgroundColor: "#1f2937", color: "#9ca3af", border: "1px solid #374151" }}
        >
          {canAfford ? "Decline" : "Not Yet"}
        </Button>
        <Button
          onClick={handleAccept}
          disabled={!canAfford}
          className="px-8 py-3 text-lg font-bold"
          style={{
            backgroundColor: canAfford ? "#166534" : "#1a1a1a",
            color: canAfford ? "#fff" : "#525252",
            cursor: canAfford ? "pointer" : "not-allowed",
          }}
        >
          Accept ({formatMoney(price)})
        </Button>
      </div>
    </div>
  );

  // Render result
  const renderResult = () => (
    <div className="space-y-6 text-center">
      {accepted ? (
        <div className="p-8 rounded-xl" style={{ backgroundColor: "#052e16", border: "2px solid #22c55e" }}>
          <div className="text-5xl mb-4 font-bold" style={{ color: "#22c55e" }}>WIN</div>
          <h2 className="text-3xl font-bold mb-2" style={{ color: "#22c55e" }}>HUB ACQUIRED</h2>
          <p className="text-xl mb-4" style={{ color: "#86efac" }}>{airport.name} is yours!</p>
          <div className="space-y-1 text-sm" style={{ color: "#9ca3af" }}>
            <p>Paid: <span style={{ color: "#ef4444" }}>{formatMoney(price)}</span></p>
            <p>New Balance: <span style={{ color: "#60a5fa" }}>{formatMoney(playerCash - price)}</span></p>
          </div>
        </div>
      ) : canAfford ? (
        <div className="p-8 rounded-xl" style={{ backgroundColor: "#1a1a1a", border: "2px solid #6b7280" }}>
          <div className="text-5xl mb-4 font-bold" style={{ color: "#9ca3af" }}>---</div>
          <h2 className="text-3xl font-bold mb-2" style={{ color: "#9ca3af" }}>OFFER DECLINED</h2>
          <p className="text-xl" style={{ color: "#6b7280" }}>
            Maybe another time. The airport will remember your success.
          </p>
        </div>
      ) : (
        <div className="p-8 rounded-xl" style={{ backgroundColor: "#1c1302", border: "2px solid #f59e0b" }}>
          <div className="text-5xl mb-4 font-bold" style={{ color: "#f59e0b" }}>$$$</div>
          <h2 className="text-3xl font-bold mb-2" style={{ color: "#f59e0b" }}>OFFER SAVED</h2>
          <p className="text-xl mb-4" style={{ color: "#fcd34d" }}>
            {airport.name} will wait for you!
          </p>
          <p className="text-sm" style={{ color: "#9ca3af" }}>
            You need {formatMoney(price - playerCash)} more to accept this offer.
            <br />
            Check "Pending Hub Offers" when you're ready.
          </p>
        </div>
      )}

      <Button
        onClick={handleClose}
        className="px-8 py-4 text-lg font-bold"
        style={{ backgroundColor: accepted ? "#166534" : "#1e40af", color: "#fff" }}
      >
        {accepted ? "Start Operating" : "Close"}
      </Button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0, 0, 0, 0.9)" }}>
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: "#0a0a0a", border: "2px solid #333" }}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle style={{ color: "#fff" }}>Hub Opportunity</CardTitle>
          {phase === "offer" && (
            <button onClick={() => handleDecline()} className="p-2 rounded-lg hover:bg-gray-800 text-xl" style={{ color: "#9ca3af" }}>X</button>
          )}
        </CardHeader>
        <CardContent className="pb-8">
          {phase === "offer" && renderOffer()}
          {phase === "result" && renderResult()}
        </CardContent>
      </Card>
    </div>
  );
}

// Export constants for use elsewhere
export const EXCLUSIVE_HUB_LIMIT = 3; // First 3 hubs are exclusive offers
export const HUB_CAPACITY = 30; // Max aircraft per hub
export { EXCLUSIVE_HUB_PRICES };
